import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth, homeForRole } from "@/lib/auth";
import { ProfileProvider } from "@/components/ProfileDrawer";
import { Toaster } from "@/components/ui/sonner";

import { Landing } from "@/pages/Landing";
import { About } from "@/pages/About";
import { Contact } from "@/pages/Contact";
import { Login } from "@/pages/Login";

import { AdminLayout } from "@/pages/admin/AdminLayout";
import { Dashboard } from "@/pages/admin/Dashboard";
import { Guards } from "@/pages/admin/Guards";
import { Clients } from "@/pages/admin/Clients";
import { Contracts } from "@/pages/admin/Contracts";
import { Trainings } from "@/pages/admin/Trainings";
import { Shifts } from "@/pages/admin/Shifts";
import { Tracking } from "@/pages/admin/Tracking";
import { Tours } from "@/pages/admin/Tours";
import { ReportsAdmin } from "@/pages/admin/ReportsAdmin";
import { Dispatch } from "@/pages/admin/Dispatch";
import { Messenger } from "@/pages/admin/Messenger";
import { PostOrdersAdmin } from "@/pages/admin/PostOrdersAdmin";
import { TasksAdmin } from "@/pages/admin/TasksAdmin";
import { InvoicesAdmin } from "@/pages/admin/InvoicesAdmin";
import { InboxAdmin } from "@/pages/admin/InboxAdmin";
import { NotificationsAdmin } from "@/pages/admin/NotificationsAdmin";
import { SettingsAdmin } from "@/pages/admin/SettingsAdmin";

import { GuardLayout } from "@/pages/guard/GuardLayout";
import { GuardHome } from "@/pages/guard/GuardHome";
import { GuardShifts } from "@/pages/guard/GuardShifts";
import { GuardTour } from "@/pages/guard/GuardTour";
import { GuardReports } from "@/pages/guard/GuardReports";
import { GuardTasks } from "@/pages/guard/GuardTasks";
import { GuardPostOrders } from "@/pages/guard/GuardPostOrders";
import { GuardMessenger } from "@/pages/guard/GuardMessenger";
import { GuardProfile } from "@/pages/guard/GuardProfile";
import { GuardTraining } from "@/pages/guard/GuardTraining";

import { PortalLayout } from "@/pages/portal/PortalLayout";
import { PortalDashboard } from "@/pages/portal/PortalDashboard";
import { PortalTracking } from "@/pages/portal/PortalTracking";
import { PortalReports } from "@/pages/portal/PortalReports";
import { PortalSchedule } from "@/pages/portal/PortalSchedule";
import { PortalInvoices } from "@/pages/portal/PortalInvoices";
import { PortalContracts } from "@/pages/portal/PortalContracts";

const ADMIN_ALLOW = ["super_admin", "ops_manager", "supervisor"];
const GUARD_ALLOW = ["guard"];
const CLIENT_ALLOW = ["client_admin", "client_viewer"];

const PUBLIC_ROUTES = [
  { path: "/", element: <Landing /> },
  { path: "/about", element: <About /> },
  { path: "/contact", element: <Contact /> },
  { path: "/login", element: <Login /> },
];

const ADMIN_ROUTES = [
  { index: true, element: <Dashboard /> },
  { path: "guards", element: <Guards /> },
  { path: "clients", element: <Clients /> },
  { path: "contracts", element: <Contracts /> },
  { path: "trainings", element: <Trainings /> },
  { path: "shifts", element: <Shifts /> },
  { path: "tracking", element: <Tracking /> },
  { path: "tours", element: <Tours /> },
  { path: "reports", element: <ReportsAdmin /> },
  { path: "dispatch", element: <Dispatch /> },
  { path: "messenger", element: <Messenger /> },
  { path: "post-orders", element: <PostOrdersAdmin /> },
  { path: "tasks", element: <TasksAdmin /> },
  { path: "invoices", element: <InvoicesAdmin /> },
  { path: "inbox", element: <InboxAdmin /> },
  { path: "notifications", element: <NotificationsAdmin /> },
  { path: "settings", element: <SettingsAdmin /> },
];

const GUARD_ROUTES = [
  { index: true, element: <GuardHome /> },
  { path: "shifts", element: <GuardShifts /> },
  { path: "tour", element: <GuardTour /> },
  { path: "reports", element: <GuardReports /> },
  { path: "tasks", element: <GuardTasks /> },
  { path: "training", element: <GuardTraining /> },
  { path: "post-orders", element: <GuardPostOrders /> },
  { path: "messenger", element: <GuardMessenger /> },
  { path: "profile", element: <GuardProfile /> },
];

const PORTAL_ROUTES = [
  { index: true, element: <PortalDashboard /> },
  { path: "tracking", element: <PortalTracking /> },
  { path: "reports", element: <PortalReports /> },
  { path: "schedule", element: <PortalSchedule /> },
  { path: "invoices", element: <PortalInvoices /> },
  { path: "contracts", element: <PortalContracts /> },
  { path: "contracts/:id", element: <PortalContracts /> },
];

const RoleRoute = ({ allow, children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-navy" />;
  if (!user) return <Navigate to="/login" replace />;
  if (allow && !allow.includes(user.role)) return <Navigate to={homeForRole(user.role)} replace />;
  return children;
};

const renderChild = (r, i) =>
  r.index
    ? <Route key="_index" index element={r.element} />
    : <Route key={r.path || i} path={r.path} element={r.element} />;

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ProfileProvider>
          <BrowserRouter>
            <Routes>
              {PUBLIC_ROUTES.map(renderChild)}
              <Route path="/admin" element={<RoleRoute allow={ADMIN_ALLOW}><AdminLayout /></RoleRoute>}>
                {ADMIN_ROUTES.map(renderChild)}
              </Route>
              <Route path="/guard" element={<RoleRoute allow={GUARD_ALLOW}><GuardLayout /></RoleRoute>}>
                {GUARD_ROUTES.map(renderChild)}
              </Route>
              <Route path="/portal" element={<RoleRoute allow={CLIENT_ALLOW}><PortalLayout /></RoleRoute>}>
                {PORTAL_ROUTES.map(renderChild)}
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster theme="dark" position="top-right" richColors />
          </BrowserRouter>
        </ProfileProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
